/**
 * Lovasok Marketplace Formatters
 *
 * Utility functions for formatting data consistently across all components.
 *
 * @package Lovasok_Marketplace
 */

/**
 * Format price in Hungarian Forint
 *
 * @param {number} price - Price value
 * @param {boolean} showFree - Show fallback for 0/null
 * @param {string} priceStatus - Listing price status (e.g. ar, ar_nelkul, arkat)
 * @returns {string} - Formatted price string
 */
export function formatPrice(price, showFree = true, priceStatus = '', priceCategory = '', currency = 'Ft') {
    if (priceStatus === 'ar_nelkul') {
        return 'Ár megegyezés szerint';
    }

    if (priceStatus === 'arkat' && priceCategory) {
        return priceCategory;
    }

    if (!price || price === 0) {
        return showFree ? 'Ár egyeztetés szerint' : '0 Ft';
    }

    // Ismeretlen/érvénytelen currency kódokat (pl. 'D', '') Ft-re normalizáljuk
    const VALID_CURRENCIES = ['HUF', 'EUR', 'USD', 'GBP', 'CHF', 'CZK', 'PLN', 'RON'];
    const normalizedCurrency = (currency === 'Ft' || currency === 'HUF') ? 'HUF'
        : (VALID_CURRENCIES.includes(String(currency).toUpperCase()) ? String(currency).toUpperCase() : 'HUF');

    try {
        const formatted = new Intl.NumberFormat('hu-HU', {
            style: 'currency',
            currency: normalizedCurrency,
            maximumFractionDigits: 0
        }).format(price);

        // Forintnál szóközös "Ft"-re cseréljük a szabvány "HUF"-ot (globálisan)
        if (normalizedCurrency === 'HUF') {
            return formatted.replace(/HUF/g, 'Ft');
        }

        return formatted;
    } catch {
        // Fallback: kézi formázás
        const numFormatted = new Intl.NumberFormat('hu-HU').format(price);
        return `${numFormatted} Ft`;
    }
}

/**
 * Format date in Hungarian format
 *
 * @param {string|Date} date - Date string or Date object
 * @param {boolean} includeTime - Include time in output
 * @returns {string} - Formatted date string
 */
export function formatDate(date, includeTime = false) {
    if (!date) return '';

    const dateObj = typeof date === 'string' ? new Date(date) : date;

    const options = {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    };

    if (includeTime) {
        options.hour = '2-digit';
        options.minute = '2-digit';
    }

    return new Intl.DateTimeFormat('hu-HU', options).format(dateObj);
}

/**
 * Format relative time (e.g., "5 perce", "2 órája")
 *
 * @param {string|Date} date - Date string or Date object
 * @returns {string} - Relative time string
 */
export function formatRelativeTime(date) {
    if (!date) return '';

    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diffMs = now - dateObj;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'most';
    if (diffMins < 60) return `${diffMins} perce`;
    if (diffHours < 24) return `${diffHours} órája`;
    if (diffDays < 7) return `${diffDays} napja`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} hete`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} hónapja`;
    return `${Math.floor(diffDays / 365)} éve`;
}

/**
 * Format number with thousands separator
 *
 * @param {number} num - Number to format
 * @returns {string} - Formatted number string
 */
export function formatNumber(num) {
    if (num === null || num === undefined) return '0';
    return new Intl.NumberFormat('hu-HU').format(num);
}

/**
 * Truncate text with ellipsis
 *
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} - Truncated text
 */
export function truncateText(text, maxLength = 100) {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
}

/**
 * Capitalize first letter
 *
 * @param {string} str - String to capitalize
 * @returns {string} - Capitalized string
 */
export function capitalize(str) {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Format file size in bytes to human readable
 *
 * @param {number} bytes - File size in bytes
 * @param {number} decimals - Number of decimals
 * @returns {string} - Formatted file size
 */
export function formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

/**
 * Debounce function execution
 *
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} - Debounced function
 */
export function debounce(func, wait = 300) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}
